import * as React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Input from '@mui/material/Input';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import QrCodeIcon from '@mui/icons-material/QrCode';
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs, { Dayjs } from 'dayjs';
import { Survey } from './TableSurveysRow';
import { Team } from './TableTeamsRow';

const toDayjs = (v: string | null | undefined): Dayjs | null => {
  if (!v) return null;
  return dayjs(v, 'YYYY-MM-DD');
};

interface CohortSurveyItemProps {
  survey: Survey;
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  setQrModalOpen: (link: string) => void;
  loadingParticipants: string | number;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
  onRemove?: () => void;
}

const CohortSurveyItem: React.FC<CohortSurveyItemProps> = ({
  survey,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  setQrModalOpen,
  loadingParticipants,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
  onRemove,
}) => {
  const { t } = useTranslation();
  const [editMode, setEditMode] = React.useState(false);
  const [newLeader, setNewLeader] = React.useState('');
  const [tooltipOpen, setTooltipOpen] = React.useState(false);
  // Report/reminder dates are ignored for cohort surveys (see cohort-report-mailer /
  // cohort-reminder-mailer, which are driven by the cohort's own dates instead). Not
  // shown in this UI, but kept in state and round-tripped unchanged on save so editing
  // the leader here doesn't clobber whatever is already stored for these fields.
  const [currentReportDate, setCurrentReportDate] = React.useState<Dayjs | null>(null);
  const [currentReminderDate, setCurrentReminderDate] = React.useState<Dayjs | null>(null);

  const existing = leaderBySurvey[survey.i.toString()] ?? {};

  // Load participants + leader data on first render
  React.useEffect(() => {
    getParticipants(survey.i);
    getLeaderAndReportDate(survey.i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [survey.i]);

  const resetEditFields = React.useCallback(() => {
    setNewLeader(existing.leader ?? '');
    setCurrentReportDate(toDayjs(existing.reportDate));
    setCurrentReminderDate(toDayjs(existing.reminderDate));
  }, [existing.leader, existing.reportDate, existing.reminderDate]);

  const openEdit = () => {
    resetEditFields();
    setEditMode(true);
  };

  return (
    <Card variant="outlined" sx={{ mb: 1, backgroundColor: 'action.hover' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        {/* Row 1: survey ID link, QR icon, survey name, analyze */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          sx={{ mb: 0.5 }}
          paddingBottom={2}
        >
          <Tooltip
            onClose={() => setTooltipOpen(false)}
            open={tooltipOpen}
            disableFocusListener
            disableHoverListener
            disableTouchListener
            title={t('surveys.link_copied')}
            placement="top"
            slotProps={{ popper: { disablePortal: true } }}
          >
            <Button
              size="small"
              variant="text"
              sx={{ minWidth: 0, px: 0.5 }}
              onClick={() => {
                setTooltipOpen(true);
                navigator.clipboard.writeText('https://mgmtlab.limequery.com/' + survey.i);
                setTimeout(() => setTooltipOpen(false), 2000);
              }}
            >
              {survey.i}
            </Button>
          </Tooltip>
          <IconButton
            size="small"
            color="primary"
            onClick={() => setQrModalOpen('https://mgmtlab.limequery.com/' + survey.i)}
          >
            <QrCodeIcon fontSize="small" />
          </IconButton>
          <Typography variant="body1" sx={{ flex: 1, minWidth: 0, fontWeight: 500 }}>
            {survey.t}
          </Typography>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {loadingParticipants.toString() !== survey.i.toString() &&
              participantsBySurvey[survey.i.toString()] && (
                <Typography variant="body2" color="text.secondary">
                  {t('surveys.participants', {
                    count: participantsBySurvey[survey.i.toString()]['completed_responses'],
                  })}
                </Typography>
              )}
            <IconButton
              size="small"
              loading={loadingParticipants.toString() === survey.i.toString()}
              color="primary"
              onClick={() => getParticipants(survey.i)}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Stack>
          <Button
            size="small"
            loading={loading.toString() === survey.i.toString()}
            variant="outlined"
            onClick={() => analyzeSurvey(survey.i)}
          >
            {t('surveys.analyze_responses')}
          </Button>
        </Stack>

        <Divider />

        {/* Row 2: leader, edit button */}
        {!editMode ? (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent="space-between"
            flexWrap="wrap"
            sx={{ mb: 0.5 }}
            paddingTop={2}
          >
            {existing.leader && (
              <Typography variant="body2" sx={{ minWidth: 0 }}>
                {t('surveys.leader_label', { leader: existing.leader })}
              </Typography>
            )}
            <Stack direction="row" spacing={2}>
              <Tooltip title={t('surveys.edit_leader_report')}>
                <span>
                  <IconButton
                    size="small"
                    disabled={loadingLeader.toString() === survey.i.toString()}
                    onClick={openEdit}
                    sx={{ ml: 'auto' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {onRemove && (
                <Tooltip title={t('common.remove')}>
                  <IconButton size="small" onClick={onRemove}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            flexWrap="wrap"
            alignItems="flex-start"
            sx={{ mb: 0.5 }}
            paddingTop={2}
          >
            <FormControl sx={{ flex: 1, minWidth: 180 }}>
              <Input
                value={newLeader}
                placeholder={t('surveys.new_leader_email')}
                onChange={(e) => setNewLeader(e.target.value)}
              />
              <FormHelperText>{t('surveys.new_leader_email_hint')}</FormHelperText>
            </FormControl>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="text"
                color="inherit"
                onClick={() => setEditMode(false)}
              >
                {t('common.cancel')}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  editLeaderReportDate(survey, newLeader, currentReportDate, currentReminderDate);
                  setEditMode(false);
                }}
              >
                {t('common.confirm')}
              </Button>
            </Stack>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

interface CohortTeamSectionProps {
  team: Team;
  allSurveys: Survey[];
  assignedSurveyIds: Set<string>;
  onEditTeam: (team: Team, name: string, leader: string) => Promise<void>;
  onDeleteTeam: (teamId: string, deleteSurveys: boolean) => Promise<void>;
  onAddSurveyToTeam: (teamId: string, surveyIds: string[]) => Promise<void>;
  onRemoveSurveyFromTeam: (teamId: string, surveyId: string) => Promise<void>;
  onRemoveFromCohort?: () => void;
  analyzeSurvey: (surveyId: string | number) => void;
  loading: string | number;
  getParticipants: (surveyId: string | number) => void;
  participantsBySurvey: { [key: string]: any };
  setQrModalOpen: (link: string) => void;
  loadingParticipants: string | number;
  isMobile: boolean;
  editLeaderReportDate: (
    survey: Survey,
    leader: string,
    reportDate: Dayjs | null,
    reminderDate: Dayjs | null
  ) => void;
  getLeaderAndReportDate: (surveyId: string | number) => void;
  loadingLeader: string | number;
  leaderBySurvey: { [key: string]: any };
}

export const CohortTeamSection: React.FC<CohortTeamSectionProps> = ({
  team,
  allSurveys,
  assignedSurveyIds,
  onEditTeam,
  onDeleteTeam,
  onAddSurveyToTeam,
  onRemoveSurveyFromTeam,
  onRemoveFromCohort,
  analyzeSurvey,
  loading,
  getParticipants,
  participantsBySurvey,
  setQrModalOpen,
  loadingParticipants,
  isMobile,
  editLeaderReportDate,
  getLeaderAndReportDate,
  loadingLeader,
  leaderBySurvey,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [editName, setEditName] = React.useState(team.name);
  const [editLeader, setEditLeader] = React.useState(team.leader.join(', '));
  const [editLoading, setEditLoading] = React.useState(false);
  const [deleteLoading, setDeleteLoading] = React.useState(false);
  const [addSurveyLoading, setAddSurveyLoading] = React.useState(false);
  const [removingId, setRemovingId] = React.useState<string | null>(null);
  const [removeConfirmId, setRemoveConfirmId] = React.useState<string | null>(null);
  const [selectedSurveys, setSelectedSurveys] = React.useState<Survey[]>([]);

  const teamSurveys = allSurveys.filter((s) => team.surveyIds.includes(String(s.i)));
  const availableSurveys = allSurveys
    .filter((s) => !assignedSurveyIds.has(String(s.i)))
    .sort((a, b) => a.t.localeCompare(b.t));

  const handleEdit = async () => {
    setEditLoading(true);
    await onEditTeam(team, editName, editLeader);
    setEditLoading(false);
    setEditDialogOpen(false);
  };

  const handleDelete = async (deleteSurveys: boolean) => {
    setDeleteLoading(true);
    await onDeleteTeam(team.id, deleteSurveys);
    setDeleteLoading(false);
    setDeleteDialogOpen(false);
  };

  const handleAddSurvey = async () => {
    if (selectedSurveys.length === 0) return;
    setAddSurveyLoading(true);
    await onAddSurveyToTeam(
      team.id,
      selectedSurveys.map((s) => String(s.i))
    );
    setSelectedSurveys([]);
    setAddSurveyLoading(false);
  };

  const handleRemoveSurvey = async (surveyId: string) => {
    setRemovingId(surveyId);
    await onRemoveSurveyFromTeam(team.id, surveyId);
    setRemovingId(null);
  };

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        mb: 1,
        backgroundColor: 'background.default',
      }}
    >
      {/* Team header — always visible, click to expand */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1, py: 0.5, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setOpen((v) => !v)}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
        </IconButton>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={500} noWrap>
            {team.name}
          </Typography>
          {!isMobile && team.leader.length > 0 && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {team.leader.join(', ')}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setEditName(team.name);
            setEditLeader(team.leader.join(', '));
            setSelectedSurveys([]);
            setEditDialogOpen(true);
          }}
        >
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setDeleteDialogOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        {onRemoveFromCohort && (
          <Tooltip title={t('cohorts.remove_team_from_cohort')}>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFromCohort();
              }}
            >
              <LinkOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      {/* Expanded content — surveys always fully visible */}
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Divider />
        <Box sx={{ px: 2, py: 1 }}>
          {teamSurveys.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('teams.no_surveys')}
            </Typography>
          ) : (
            teamSurveys.map((s) => (
              <CohortSurveyItem
                key={s.i}
                survey={s}
                analyzeSurvey={analyzeSurvey}
                loading={loading}
                getParticipants={getParticipants}
                participantsBySurvey={participantsBySurvey}
                setQrModalOpen={setQrModalOpen}
                loadingParticipants={loadingParticipants}
                editLeaderReportDate={editLeaderReportDate}
                getLeaderAndReportDate={getLeaderAndReportDate}
                loadingLeader={loadingLeader}
                leaderBySurvey={leaderBySurvey}
                onRemove={
                  removingId === String(s.i) ? undefined : () => setRemoveConfirmId(String(s.i))
                }
              />
            ))
          )}
        </Box>
      </Collapse>

      {/* Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>{t('teams.edit')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1, minWidth: 320 }}>
            <Input
              value={editName}
              placeholder={t('teams.name')}
              onChange={(e) => setEditName(e.target.value)}
            />
            <FormControl>
              <Input
                value={editLeader}
                placeholder={t('teams.leader')}
                onChange={(e) => setEditLeader(e.target.value)}
              />
              <FormHelperText>{t('teams.leader_hint')}</FormHelperText>
            </FormControl>
            <Divider />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Autocomplete
                multiple
                size="small"
                options={availableSurveys}
                getOptionLabel={(s) => `${s.t} (${s.i})`}
                value={selectedSurveys}
                onChange={(_, v) => setSelectedSurveys(v)}
                renderInput={(params) => (
                  <TextField {...params} placeholder={t('teams.add_survey')} variant="standard" />
                )}
                sx={{ flex: 1 }}
                isOptionEqualToValue={(a, b) => a.i === b.i}
              />
              <Button
                size="small"
                variant="outlined"
                disabled={selectedSurveys.length === 0 || addSurveyLoading}
                onClick={handleAddSurvey}
              >
                {t('teams.add_survey')}
              </Button>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleEdit} disabled={editLoading || !editName}>
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove survey confirm dialog */}
      <Dialog open={removeConfirmId !== null} onClose={() => setRemoveConfirmId(null)}>
        <DialogTitle>{t('teams.remove_survey_confirm_title')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setRemoveConfirmId(null)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            onClick={() => {
              const id = removeConfirmId!;
              setRemoveConfirmId(null);
              handleRemoveSurvey(id);
            }}
          >
            {t('common.remove')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('teams.delete_confirm_title')}</DialogTitle>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={() => handleDelete(false)} disabled={deleteLoading}>
            {t('teams.delete_keep_surveys')}
          </Button>
          <Button onClick={() => handleDelete(true)} color="error" disabled={deleteLoading}>
            {t('teams.delete_also_surveys')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
